import React from "react";
import { useTheme } from "../../theme/themeContext";

const ThemeCustomizer = () => {
  const {
    preferences,
    presets,
    panelOpen,
    setPanelOpen,
    applyPreset,
    applyTemplate,
    toggleMode,
    templates,
    updateCustomColor,
  } = useTheme();

  return (
    <aside className="fixed bottom-4 left-4 z-30 w-[calc(100vw-2rem)] max-w-sm">
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-3xl border border-white/15 bg-white/10 px-4 py-3 text-left text-sm font-medium text-skin-text shadow-glass backdrop-blur-xl"
        onClick={() => setPanelOpen((current) => !current)}
        aria-expanded={panelOpen}
      >
        <span className="font-display text-base">Theme Studio</span>
        <span className="rounded-full bg-[var(--accent-glow)] px-3 py-1 text-xs uppercase tracking-[0.24em] text-skin-accentSoft">
          {preferences.mode}
        </span>
      </button>

      {panelOpen ? (
        <div className="mt-3 max-h-[calc(100vh-7rem)] overflow-y-auto rounded-[1.75rem] border border-white/15 bg-white/10 p-4 text-skin-text shadow-glass backdrop-blur-xl">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-skin-accentSoft">
                Live Theme Controls
              </p>
              <h3 className="font-display text-xl font-semibold">Design template</h3>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-skin-text transition hover:bg-white/15"
                onClick={toggleMode}
              >
                {preferences.mode === "dark" ? "Switch to Light" : "Switch to Dark"}
              </button>
              <button
                type="button"
                className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-skin-text transition hover:bg-white/15"
                onClick={() => setPanelOpen(false)}
                aria-label="Close theme customizer"
              >
                Close
              </button>
            </div>
          </div>

          <div className="mb-4">
            <span className="mb-3 block text-sm text-skin-muted">Design templates</span>
            <div className="grid gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className={`rounded-2xl border px-4 py-3 text-left transition ${
                    template.id === preferences.templateId
                      ? "border-[var(--accent)] bg-[var(--accent-glow)]"
                      : "border-white/15 bg-white/10 hover:bg-white/15"
                  }`}
                  onClick={() => applyTemplate(template.id)}
                >
                  <span className="block text-sm font-semibold text-skin-text">
                    {template.name}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-skin-muted">
                    {template.description}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <span className="mb-3 block text-sm text-skin-muted">Preset colors</span>
            <div className="grid grid-cols-4 gap-3">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`aspect-square rounded-2xl border-2 transition ${
                    preset.id === preferences.themeId
                      ? "border-white shadow-lg"
                      : "border-transparent"
                  }`}
                  onClick={() => applyPreset(preset.id)}
                  title={preset.name}
                  style={{
                    background: `linear-gradient(135deg, ${preset.accent}, ${preset.accentSoft})`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-2 text-sm text-skin-muted">
              <span>Primary Accent</span>
              <input
                type="color"
                className="h-12 w-full rounded-2xl border border-white/15 bg-transparent"
                value={preferences.accent}
                onChange={(event) => updateCustomColor("accent", event.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-skin-muted">
              <span>Secondary Accent</span>
              <input
                type="color"
                className="h-12 w-full rounded-2xl border border-white/15 bg-transparent"
                value={preferences.accentSoft}
                onChange={(event) =>
                  updateCustomColor("accentSoft", event.target.value)
                }
              />
            </label>
          </div>

          <p className="mt-4 text-sm leading-6 text-skin-muted">
            Theme changes save locally now and sync to Firebase when your Firestore
            env keys are added.
          </p>
        </div>
      ) : null}
    </aside>
  );
};

export default ThemeCustomizer;
